import { Link, useLocation } from '@tanstack/react-router';
import {
  User,
  Users,
  CreditCard,
  Plus,
  PackagePlus,
  HardDrive,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '../store/auth.store';
import EndpointSvg from '../components/svg/Endpoint.svg?react';
import Tenant from '../components/svg/Tenant.svg?react';
import Plans from '../components/svg/Plans.svg?react';
import Team from '../components/svg/Team.svg?react';
import Impersonate from '../components/svg/Impersonate.svg?react';

// vite-plugin-svgr emits an <svg> with only a viewBox (no width/height) and it
// ignores lucide's `size` prop, so it renders at its default size and balloons.
// Wrap it to accept `size` like the lucide icons. viewBox is 12×14, so derive
// width from height to avoid stretching it square.
function Endpoint({
  size = 20,
  ...props
}: React.SVGProps<SVGSVGElement> & { size?: number }) {
  return <EndpointSvg height={size} width={(size * 12) / 14} {...props} />;
}

type BottomNavItem = {
  label: string;
  path: string;
  icon: LucideIcon | any;
  adminOnly?: boolean;
};

const bottomNavItems: BottomNavItem[] = [
  { label: 'Tenants', path: '/', icon: Tenant },
  { label: 'Plans', path: '/plans', icon: Plans, adminOnly: true },
  { label: 'New', path: '/customers/new', icon: Plus },
  {
    label: 'Impersonate',
    path: '/impersonate',
    icon: Impersonate,
    adminOnly: true,
  },
  { label: 'Storage', path: '/s3-endpoints', icon: Endpoint, adminOnly: true },
  { label: 'Team', path: '/team', icon: Team, adminOnly: true },
];

export function BottomNav() {
  const role = useAuthStore((s) => s.role);
  const { pathname } = useLocation();

  const visibleItems = bottomNavItems.filter(
    (item) => !item.adminOnly || role === 'admin',
  );

  return (
    <nav className="border-primary/12 bg-bg fixed right-0 bottom-0 left-0 z-50 flex h-16 items-center border-t md:hidden">
      {visibleItems.map((item) => {
        const active = pathname === item.path;
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            className={[
              'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[0.625rem] font-medium transition-colors',
              active ? 'text-accent' : 'text-primary/50',
            ].join(' ')}
          >
            <Icon size={20} strokeWidth={active ? 2 : 1.5} />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
